import debounce from 'lodash.debounce';
import { gspx } from '@rsos/gainsight';

// FYI: this is the event_name and attributes_category column in BigQuery.
const CATEGORY = 'dsl';

const DATA_MAP_CATEGORY = 'Data Map';
const SPATIAL_LAYER_CATEGORY = 'Spatial Layer';

/**
 * Track hover on a map marker.
 * @param {string} layerKey
 * @param {string} [status] - The status of the marker, if applicable.
 */
export const trackMarkerHover = debounce((layerKey, info) => {
  const event = {
    name: `hover for ${layerKey || 'unknown'}`,
    ...info,
  };
  return gspx.trackCustomEvent(CATEGORY, event);
}, 500);

/**
 * Track click on map markers.
 * @param {string} layerKey
 * @param {string} [status] - The status of the marker, if applicable.
 */
export const trackMarkerClick = (layerKey, info) => {
  const event = {
    name: `click for ${layerKey || 'unknown'}`,
    ...info,
  };
  return gspx.trackCustomEvent(CATEGORY, event);
};

/**
 * Track the total count of spatial objects in a snapshot.
 * @param {string} layerKey - The layer key for the snapshot.
 * @param {number} timestamp - Timestamp to identify the snapshot.
 * @param {number} count - The count of objects in the snapshot.
 * @param {boolean} visible - The visibility for the layer.
 */
const trackSnapshotTotalCount = (layerKey, timestamp, count, visible) => {
  return gspx.trackCustomEvent(CATEGORY, {
    name: `snapshot total for ${layerKey || 'unknown'}`,
    count,
    'Launched date': timestamp,
    visible,
  });
};

/**
 * Track the count of spatial objects in a snapshot grouped by a status.
 * @param {string} layerKey - The layer key for the snapshot.
 * @param {number} timestamp - Timestamp to identify the snapshot.
 * @param {number} count - The count of objects in the snapshot.
 * @param {string} status - The status for the group in the snapshot.
 * @param {boolean} visible - The visibility for the layer.
 */
const trackSnapshotCountByStatus = (
  layerKey,
  timestamp,
  count,
  status,
  visible,
) => {
  return gspx.trackCustomEvent(CATEGORY, {
    name: `snapshot count for ${layerKey || 'unknown'}`,
    count,
    status,
    'Launched date': timestamp,
    visible,
  });
};

/**
 * Tracks a total count and invidividual counts grouped by status. The status
 * is picked out from the snapshot using selectStatus, which should be provided
 * by the caller of the function.
 * @param {object} config
 * @param {string} config.layerKey - The layerKey for the snapshot.
 * @param {array} config.data - An array of snapshot data.
 * @param {function} config.selectStatus - A function to pick out the status value.
 * @param {boolean} config.visible- The visibility of the layer.
 */
export const trackSnapshot = async ({
  layerKey,
  data = [],
  selectStatus = () => '',
  visible,
}) => {
  // Use the same timestamp for all records.
  const snapshotTime = Date.now();

  // Iterate through the data and group counts by status.
  const counts = data.reduce((o, current) => {
    const status = selectStatus(current);
    const key = `${status.toLowerCase()}`;
    if (!o[key] && o[key] !== 0) {
      o[key] = 0;
    }
    o[key] += 1;
    return o;
  }, {});

  // Track individual counts by status.
  let total = 0;
  for (const status in counts) {
    const count = counts[status];
    total += count;
    trackSnapshotCountByStatus(layerKey, snapshotTime, count, status, visible);
  }

  // Track a total count.
  trackSnapshotTotalCount(layerKey, snapshotTime, total, visible);
};

export const trackDeckGLHoverOnSpatialObject = debounce(
  ({ irpVersion, externalId, source }) => {
    gspx.trackCustomEvent(DATA_MAP_CATEGORY, {
      irpVersion,
      name: `${source} hover on spatial object ${externalId}`,
      Category: SPATIAL_LAYER_CATEGORY,
      'Launched date': new Date(),
    });
  },
  500,
);

export const trackDeckGLClickOnSpatialObject = debounce(
  ({ irpVersion, externalId, source }) => {
    gspx.trackCustomEvent(DATA_MAP_CATEGORY, {
      name: `Click on ${source} spatial object ${externalId}`,
      Category: SPATIAL_LAYER_CATEGORY,
      'Launched date': new Date(),
      irpVersion,
    });
  },
  500,
);

export const trackActiveGeofenceHover = debounce(irpVersion => {
  gspx.trackCustomEvent(DATA_MAP_CATEGORY, {
    irpVersion,
    name: `Hover an active geofence`,
    Category: 'ESRI Map',
    'Launched date': new Date(),
  });
}, 500);
